'use client';

import { useAuth0 } from '@auth0/auth0-react';
import omit from 'lodash/omit';
import { useSearchParams } from 'next/navigation';
import { ReactElement, useEffect } from 'react';

import FullPageLoader from '@/components/ui/FullPageLoader';
import { EnvConfig } from '@/env_config';
import { useUser } from '@/hooks/store';
import { protectOpenRedirect } from '@/utils/protectOpenRedirect';

export default function Login(): ReactElement {
	const searchParams = useSearchParams();
	const rawEmailFromSearch = searchParams?.getAll('email') ?? [];
	const emailFromSearch = rawEmailFromSearch[0] ?? undefined;
	const returnTo = searchParams?.get('returnTo');

	const { loginWithRedirect, logout, isLoading, isAuthenticated } = useAuth0();
	const { user, loading: isUserLoading } = useUser();

	useEffect(() => {
		// If Auth0 or user state is still loading, wait
		if (isLoading || isUserLoading) {
			return;
		}

		// If user already has an access token in Redux, redirect them away from login
		if (user.accessToken) {
			const redirectUrl = returnTo ? protectOpenRedirect(decodeURIComponent(returnTo)) : '/groups';
			window.location.assign(redirectUrl);
			return;
		}

		// If Auth0 says authenticated but no token in Redux yet, wait for user loading to complete
		// Only logout if we're certain the user state has been initialized but there's no token
		// This prevents logout loops during the OAuth callback process
		if (isAuthenticated && !isUserLoading) {
			logout({
				logoutParams: { returnTo: `${EnvConfig.partnerPortalUrl}/login` },
			}).catch(() => {});
		} else if (!isAuthenticated) {
			// Not authenticated, trigger login flow
			loginWithRedirect({
				authorizationParams: { login_hint: emailFromSearch ?? '' },
				// omit email from the searchParams to avoid it being carried further than required
				appState: { query: omit(Object.fromEntries(searchParams?.entries() ?? []), ['email']) },
			}).catch(() => {});
		}
	}, [
		isLoading,
		isUserLoading,
		isAuthenticated,
		user.accessToken,
		loginWithRedirect,
		logout,
		emailFromSearch,
		searchParams,
		returnTo,
	]);

	return <FullPageLoader />;
}
